import {
  useGetSubscription,
  useListBillingPlans,
  useListPaymentMethods,
  useUpdateSubscription,
} from "@/api/stripe";
import { useOrgUsers, usePendingInvites } from "@/api/users";
import { MembersPane } from "@/components/AdminPanel/Organization/components/members/MembersPane";
import { Button } from "@/components/ui/button";
import { ROUTES } from "@/constants/routes";
import { useBetaPlan } from "@/hooks/useBetaPlan";
import { useEnterprisePlan } from "@/hooks/useEnterprisePlan";
import {
  buildPlanChangeAction,
  resolvePlanToConfigure,
  transformBillingPlans,
  type PlanSelection,
  type TransformedPlan,
} from "@/utils/planChange";
import { route } from "@/utils/route";
import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { PlanConfigurationModal } from "../../../pages/billing/PlanConfigurationModal";

const OrgUsers: React.FC = () => {
  const navigate = useNavigate();
  const {
    data: users = [],
    isLoading: isLoadingUsers,
    isError: isUsersError,
    refetch: refetchUsers,
  } = useOrgUsers();
  const {
    data: pendingInvites = [],
    isLoading: isLoadingInvites,
    isError: isInvitesError,
  } = usePendingInvites();
  const { data: subscription } = useGetSubscription();
  const { data: billingPlans } = useListBillingPlans();
  const { data: paymentMethods } = useListPaymentMethods();
  const updateSubscription = useUpdateSubscription();

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<TransformedPlan | null>(
    null,
  );

  const handleUserUpdated = () => {
    void refetchUsers();
  };

  const handleUserDeleted = () => {
    void refetchUsers();
  };

  const isLoading = isLoadingUsers || isLoadingInvites;
  const isError = isUsersError || isInvitesError;

  const currentUserCount = users.length + pendingInvites.length;
  const seatLimit = subscription?.data.stripe_subscription?.seat_count || 0;
  const isAtCapacity = seatLimit > 0 && currentUserCount >= seatLimit;
  const isEnterprisePlan = useEnterprisePlan();
  const isBetaPlan = useBetaPlan();

  const canInvite = isEnterprisePlan || !isAtCapacity;

  const transformedPlans = useMemo(
    () => transformBillingPlans(billingPlans?.data.billing_plans),
    [billingPlans],
  );

  const handleAddSeats = () => {
    const planToShow = resolvePlanToConfigure(
      transformedPlans,
      subscription?.data.stripe_subscription?.plan_name,
    );

    if (planToShow) {
      setSelectedPlan(planToShow);
      setShowPlanModal(true);
    }
  };

  const handlePlanConfigurationComplete = async (selection: PlanSelection) => {
    setShowPlanModal(false);

    const planChange = buildPlanChangeAction({
      selection,
      subscription: subscription?.data.stripe_subscription,
      paymentMethods: paymentMethods?.data.payment_methods,
    });

    if (planChange.type === "update") {
      try {
        await updateSubscription.mutateAsync(planChange.payload);
        toast.success("Plan updated successfully");
      } catch (error: any) {
        toast.error(error.message || "Failed to update plan");
      }
    } else {
      const checkoutParams: Record<string, string> = {
        plan: selection.plan.name,
        billing_cycle: selection.billing_cycle,
        user_count: String(selection.user_count),
        redirect_to: ROUTES.SETTINGS.MEMBERS,
      };

      navigate(route(ROUTES.BILLING.CHECKOUT, {}, checkoutParams));
    }
  };

  const capacityAction =
    !canInvite && !isBetaPlan ? (
      <Button variant="outline" size="sm" onClick={handleAddSeats}>
        Add Seats
      </Button>
    ) : null;

  return (
    <>
      <title>Members</title>
      <div className="space-y-6">
        {isLoading ? null : isError ? (
          <p className="text-lc-blocked dark:text-lc-blocked">
            Error loading members. Please try again.
          </p>
        ) : (
          <MembersPane
            users={users}
            pendingInvites={pendingInvites}
            seatLimit={seatLimit}
            canInvite={canInvite}
            capacityAction={capacityAction}
            onUserUpdated={handleUserUpdated}
            onUserDeleted={handleUserDeleted}
          />
        )}
      </div>

      {selectedPlan && (
        <PlanConfigurationModal
          plan={selectedPlan}
          open={showPlanModal}
          buttonLoading={updateSubscription.isPending}
          redirectTo={ROUTES.SETTINGS.MEMBERS}
          onComplete={handlePlanConfigurationComplete}
          onClose={() => setShowPlanModal(false)}
        />
      )}
    </>
  );
};

export default OrgUsers;
